import React from 'react';
import { X, Send, Calendar, FileText, Clock, Sun, SunMedium } from 'lucide-react';

const LeaveModal = ({ isOpen, onClose, onSubmit, formData, setFormData, errors, loading, calculateDays }) => {
  if (!isOpen) return null;

  const todayStr = new Date().toISOString().split('T')[0];
  const duration = formData.leaveDuration || 'Full Day';

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData(prev => {
      const updated = { ...prev, [name]: value };

      // Half Day: keep toDate locked to whatever fromDate is
      if (name === 'fromDate' && (prev.leaveDuration || 'Full Day') === 'Half Day') {
        updated.toDate = value;
      }

      return updated;
    });
  };

  const handleDurationChange = (nextDuration) => {
    setFormData(prev => ({
      ...prev,
      leaveDuration: nextDuration,
      // Switching to Half Day: To Date must match From Date
      toDate: nextDuration === 'Half Day' ? (prev.fromDate || '') : prev.toDate
    }));
  };

  const displayDays = duration === 'Half Day'
    ? 0.5
    : calculateDays(formData.fromDate, formData.toDate);

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center transition-all duration-300 sm:items-center sm:p-4 bg-black/50 dark:bg-black/70 backdrop-blur-sm">
      {/* 
        Mobile: sheet slides up from bottom, sits ABOVE the bottom nav bar
        Desktop: centered modal (fully rounded)
      */}
      <div className="
        bg-white dark:bg-gray-900
        w-full sm:max-w-md
        max-h-[calc(100dvh-72px)] sm:max-h-[90vh]
        overflow-y-auto
        rounded-t-2xl sm:rounded-2xl
        shadow-2xl
        border border-gray-100 dark:border-gray-800
        transform transition-all duration-300
        animate-in fade-in slide-in-from-bottom-4 sm:zoom-in
        mb-[72px] sm:mb-0
      ">

        {/* Drag handle — visible only on mobile */}
        <div className="flex justify-center pt-3 pb-1 sm:hidden">
          <div className="w-10 h-1 bg-gray-300 rounded-full dark:bg-gray-600" />
        </div>

        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-4 py-4 bg-white border-b border-gray-100 sm:px-6 dark:border-gray-800 dark:bg-gray-900">
          <h2 className="flex items-center gap-2 text-base font-semibold text-gray-800 sm:text-xl dark:text-white">
            <div className="p-1.5 rounded-lg bg-blue-100/20 dark:bg-blue-500/20">
              <Calendar className="w-4 h-4 text-blue-600 sm:w-5 sm:h-5 dark:text-blue-400" />
            </div>
            Apply for Leave
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200 hover:rotate-90"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={onSubmit} className="px-4 py-4 space-y-4 sm:px-6 sm:py-5 sm:space-y-5">

          {/* Leave Type */}
          <div>
            <label className="block mb-1.5 text-sm font-medium text-gray-700 dark:text-gray-300">
              Leave Type <span className="text-blue-500 dark:text-blue-400">*</span>
            </label>
            <select
              name="leaveType"
              value={formData.leaveType}
              onChange={handleInputChange}
              className={`w-full px-4 py-2.5 border rounded-xl focus:outline-none focus:ring-2 transition-all duration-200 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm ${
                errors.leaveType
                  ? 'border-red-500 dark:border-red-500 focus:ring-red-500'
                  : 'border-gray-300 dark:border-gray-700 focus:ring-blue-500 dark:focus:ring-blue-400'
              }`}
            >
              <option value="">Select Leave Type</option>
              <option value="Annual Leave">Annual Leave</option>
              <option value="Sick Leave">Sick Leave</option>
              <option value="Casual Leave">Casual Leave</option>
              <option value="Unpaid Leave">Unpaid Leave</option>
            </select>
            {errors.leaveType && <ErrorMsg message={errors.leaveType} />}
          </div>

          {/* Duration: Half Day / Full Day */}
          <div>
            <label className="block mb-1.5 text-sm font-medium text-gray-700 dark:text-gray-300">
              Duration <span className="text-blue-500 dark:text-blue-400">*</span>
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => handleDurationChange('Full Day')}
                className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-medium transition-all duration-200 ${
                  duration === 'Full Day'
                    ? 'bg-blue-500 border-blue-500 text-white shadow-lg shadow-blue-500/25'
                    : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                <SunMedium className="w-4 h-4" />
                Full Day
              </button>
              <button
                type="button"
                onClick={() => handleDurationChange('Half Day')}
                className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-medium transition-all duration-200 ${
                  duration === 'Half Day'
                    ? 'bg-blue-500 border-blue-500 text-white shadow-lg shadow-blue-500/25'
                    : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                <Sun className="w-4 h-4" />
                Half Day
              </button>
            </div>
          </div>

          {/* From & To Date — side by side on sm+, stacked on mobile */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="block mb-1.5 text-sm font-medium text-gray-700 dark:text-gray-300">
                From Date <span className="text-blue-500 dark:text-blue-400">*</span>
              </label>
              <div className="relative">
                <Calendar className="absolute w-4 h-4 text-gray-400 transform -translate-y-1/2 pointer-events-none left-3 top-1/2 dark:text-gray-500" />
                <input
                  type="date"
                  name="fromDate"
                  value={formData.fromDate}
                  onChange={handleInputChange}
                  min={todayStr}
                  className={`w-full pl-10 pr-3 py-2.5 border rounded-xl focus:outline-none focus:ring-2 transition-all duration-200 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm ${
                    errors.fromDate
                      ? 'border-red-500 dark:border-red-500 focus:ring-red-500'
                      : 'border-gray-300 dark:border-gray-700 focus:ring-blue-500 dark:focus:ring-blue-400'
                  }`}
                />
              </div>
              {errors.fromDate && <ErrorMsg message={errors.fromDate} />}
            </div>

            <div>
              <label className="block mb-1.5 text-sm font-medium text-gray-700 dark:text-gray-300">
                To Date <span className="text-blue-500 dark:text-blue-400">*</span>
              </label>
              <div className="relative">
                <Calendar className="absolute w-4 h-4 text-gray-400 transform -translate-y-1/2 pointer-events-none left-3 top-1/2 dark:text-gray-500" />
                <input
                  type="date"
                  name="toDate"
                  value={duration === 'Half Day' ? formData.fromDate : formData.toDate}
                  onChange={handleInputChange}
                  min={formData.fromDate || todayStr}
                  disabled={duration === 'Half Day'}
                  className={`w-full pl-10 pr-3 py-2.5 border rounded-xl focus:outline-none focus:ring-2 transition-all duration-200 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm ${
                    duration === 'Half Day' ? 'opacity-60 cursor-not-allowed' : ''
                  } ${
                    errors.toDate
                      ? 'border-red-500 dark:border-red-500 focus:ring-red-500'
                      : 'border-gray-300 dark:border-gray-700 focus:ring-blue-500 dark:focus:ring-blue-400'
                  }`}
                />
              </div>
              {duration === 'Half Day' && (
                <p className="mt-1.5 text-xs text-gray-400 dark:text-gray-500">
                  Locked to From Date for Half Day
                </p>
              )}
              {errors.toDate && <ErrorMsg message={errors.toDate} />}
            </div>
          </div>

          {/* Days Display */}
          {formData.fromDate && (duration === 'Half Day' || formData.toDate) && (
            <div className="p-3 transition-all duration-300 border border-blue-100 sm:p-4 bg-gradient-to-r from-blue-50 to-blue-50 dark:from-blue-900/20 dark:to-amber-900/20 rounded-xl dark:border-blue-800">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Total Working Days:</span>
                </div>
                <span className="text-lg font-bold text-blue-600 dark:text-blue-400">
                  {displayDays}
                </span>
              </div>
            </div>
          )}

          {/* Reason */}
          <div>
            <label className="block mb-1.5 text-sm font-medium text-gray-700 dark:text-gray-300">
              Reason <span className="text-blue-500 dark:text-blue-400">*</span>
            </label>
            <div className="relative">
              <FileText className="absolute w-4 h-4 text-gray-400 pointer-events-none left-3 top-3 dark:text-gray-500" />
              <textarea
                name="reason"
                value={formData.reason}
                onChange={handleInputChange}
                rows="4"
                placeholder="Please provide a detailed reason for your leave request..."
                className={`w-full pl-10 pr-4 py-2.5 border rounded-xl focus:outline-none focus:ring-2 transition-all duration-200 resize-none bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 text-sm ${
                  errors.reason
                    ? 'border-red-500 dark:border-red-500 focus:ring-red-500'
                    : 'border-gray-300 dark:border-gray-700 focus:ring-blue-500 dark:focus:ring-blue-400'
                }`}
              />
            </div>
            {errors.reason && <ErrorMsg message={errors.reason} />}
            {formData.reason && !errors.reason && (
              <p className="mt-1.5 text-xs text-right text-gray-400 dark:text-gray-500">
                {formData.reason.length} characters
              </p>
            )}
          </div>

          {/* Info Note */}
          <div className="p-3 border border-blue-100 bg-blue-100/20 dark:bg-blue-900/20 rounded-xl dark:border-blue-800">
            <p className="flex items-start gap-2 text-xs text-blue-700 dark:text-blue-400">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-500 mt-1 flex-shrink-0" />
              Leave requests should be submitted at least 3 days in advance
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2 pb-3 sm:pb-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-xl text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-all duration-200 font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-gradient-to-r from-blue-500 to-blue-500 hover:from-blue-600 hover:to-blue-600 text-white px-4 py-2.5 rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm font-medium shadow-lg shadow-blue-500/25"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white rounded-full border-t-transparent animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Submit Request
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const ErrorMsg = ({ message }) => (
  <p className="flex items-center gap-1 mt-1.5 text-xs text-red-500 dark:text-red-400">
    <span className="flex-shrink-0 inline-block w-1 h-1 bg-red-500 rounded-full" />
    {message}
  </p>
);

export default LeaveModal;